import {
  DocumentLoadError,
  isAcceptedFilename,
  makeDocument,
  upsertRecent,
  type FileSource,
  type OpenedDocument,
  type RecentEntry,
} from '@markroom/ui';
import { idbDeleteHandle, idbGetHandle, idbPutHandle } from './idb';

const RECENTS_KEY = 'markroom.recents';
const MAX_RECENTS = 20;

const supportsFsa = typeof window !== 'undefined' && 'showOpenFilePicker' in window;

// In-memory cache of handles opened this session, keyed by document id, so
// rememberRecent() can persist the handle for later reopen.
const handleCache = new Map<string, FileSystemFileHandle>();

const PICKER_TYPES = [
  {
    description: 'Markdown',
    accept: {
      'text/markdown': ['.md', '.markdown', '.mdown', '.mkd'],
      'text/plain': ['.md', '.markdown', '.mdown', '.mkd'],
    },
  },
];

function readRecents(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentEntry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as RecentEntry).id === 'string' &&
        typeof (e as RecentEntry).name === 'string' &&
        typeof (e as RecentEntry).openedAt === 'number',
    );
  } catch {
    return [];
  }
}

function writeRecents(entries: RecentEntry[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(entries.slice(0, MAX_RECENTS)));
  } catch {
    // ignore storage failures
  }
}

async function fileToDocument(file: File, handle?: FileSystemFileHandle): Promise<OpenedDocument> {
  // Central extension gate: covers the picker, the <input> fallback, and drops —
  // the FSA "All files" option / a bypassed accept filter can yield any file.
  if (!isAcceptedFilename(file.name)) {
    throw new DocumentLoadError(
      `"${file.name}" is not a Markdown file (.md, .markdown, .mdown, .mkd).`,
      'unsupported',
    );
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  const doc = await makeDocument(file.name, bytes, file.name);
  if (handle) handleCache.set(doc.id, handle);
  return doc;
}

function pickViaInput(): Promise<OpenedDocument | null> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.md,.markdown,.mdown,.mkd,text/markdown';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }
      fileToDocument(file).then(resolve, reject);
    };
    // If the dialog is cancelled there is no reliable event; the promise simply
    // never resolves, which is acceptable (no pending UI state depends on it).
    input.click();
  });
}

// --- PWA launchQueue (file_handlers) -------------------------------------
// Files the OS launched us with arrive here. We buffer them until the app
// subscribes, so a cold-start launch isn't lost before React mounts.
const launchedQueue: OpenedDocument[] = [];
let launchSubscriber: ((doc: OpenedDocument) => void) | null = null;
let launchErrorSink: ((message: string) => void) | null = null;
let launchConsumerSet = false;

function errorMessage(err: unknown): string {
  if (err instanceof DocumentLoadError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Could not open the launched file.';
}

function setupLaunchQueue(): void {
  if (launchConsumerSet) return;
  launchConsumerSet = true;
  if (typeof window === 'undefined' || !window.launchQueue) return;
  window.launchQueue.setConsumer((params) => {
    void (async () => {
      for (const handle of params.files) {
        try {
          const file = await handle.getFile();
          const doc = await fileToDocument(file, handle);
          if (launchSubscriber) launchSubscriber(doc);
          else launchedQueue.push(doc);
        } catch (err) {
          if (launchErrorSink) launchErrorSink(errorMessage(err));
        }
      }
    })();
  });
}

async function ensureReadPermission(handle: FileSystemFileHandle): Promise<boolean> {
  const opts = { mode: 'read' } as const;
  const query = await handle.queryPermission?.(opts);
  if (query === 'granted') return true;
  const request = await handle.requestPermission?.(opts);
  return request === 'granted';
}

async function ensureWritePermission(handle: FileSystemFileHandle): Promise<boolean> {
  const opts = { mode: 'readwrite' } as const;
  const query = await handle.queryPermission?.(opts);
  if (query === 'granted') return true;
  const request = await handle.requestPermission?.(opts);
  return request === 'granted';
}

/** Write `text` to `handle` (truncating) via a writable stream. */
async function writeHandle(handle: FileSystemFileHandle, text: string): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(text);
  await writable.close();
}

/** Fallback "save" where no writable handle exists (Firefox/Safari): a download. */
function downloadText(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function createWebFileSource(): FileSource {
  // Register the launch consumer ASAP so OS-launched files (cold start) buffer.
  setupLaunchQueue();
  return {
    kind: 'web',
    platform: 'web',
    canReopenRecents: supportsFsa,
    canWatch: false,
    canOpenNewWindow: true,

    async pickAndOpen() {
      if (supportsFsa && window.showOpenFilePicker) {
        let handles: FileSystemFileHandle[];
        try {
          handles = await window.showOpenFilePicker({
            multiple: false,
            excludeAcceptAllOption: true,
            types: PICKER_TYPES,
          });
        } catch (err) {
          // AbortError = user cancelled the picker.
          if (err instanceof DOMException && err.name === 'AbortError') return null;
          throw err;
        }
        const handle = handles[0];
        if (!handle) return null;
        const file = await handle.getFile();
        return fileToDocument(file, handle);
      }
      return pickViaInput();
    },

    async openRecent(entry) {
      const handle = await idbGetHandle(entry.id);
      if (!handle) return null;
      const granted = await ensureReadPermission(handle);
      if (!granted) return null;
      try {
        const file = await handle.getFile();
        return await fileToDocument(file, handle);
      } catch {
        return null; // file moved/deleted → caller prunes
      }
    },

    resolveAssetUrl(_doc, relativePath) {
      // Remote/absolute URLs are loadable as-is; pass them through. The web
      // sandbox cannot read sibling files of a picked document, so relative
      // paths are inaccessible → null (renderer shows alt text).
      return /^(https?:|data:|blob:|\/\/)/i.test(relativePath) ? relativePath : null;
    },

    openRelativeLink() {
      // A File/handle from the picker or a drop carries no directory context,
      // so the web sandbox cannot reach a sibling file. Safe no-op: the click
      // handler already preventDefault()d, so the current document stays open.
      return Promise.resolve(null);
    },

    watch() {
      return () => {};
    },

    listRecent() {
      return Promise.resolve(readRecents().sort((a, b) => b.openedAt - a.openedAt));
    },

    async rememberRecent(doc) {
      const before = readRecents();
      const entry: RecentEntry = {
        id: doc.id,
        name: doc.name,
        locator: doc.locator ?? doc.id,
        openedAt: Date.now(),
      };
      const next = upsertRecent(before, entry, MAX_RECENTS);
      writeRecents(next);

      // Persist the handle (if we have one) and prune handles for dropped entries.
      const handle = handleCache.get(doc.id);
      if (handle && supportsFsa) {
        try {
          await idbPutHandle(doc.id, handle);
        } catch {
          // ignore
        }
      }
      const keptIds = new Set(next.map((e) => e.id));
      for (const e of before) {
        if (!keptIds.has(e.id)) await idbDeleteHandle(e.id).catch(() => undefined);
      }
    },

    async forgetRecent(id) {
      writeRecents(readRecents().filter((e) => e.id !== id));
      await idbDeleteHandle(id).catch(() => undefined);
    },

    attachDropTarget(element, onOpen, onError) {
      // Route launch-read errors to the same UI error channel while mounted.
      launchErrorSink = onError;
      const onDragOver = (e: DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      };
      const onDrop = (e: DragEvent) => {
        e.preventDefault();
        void handleDrop(e, onOpen, onError);
      };
      element.addEventListener('dragover', onDragOver);
      element.addEventListener('drop', onDrop);
      return () => {
        element.removeEventListener('dragover', onDragOver);
        element.removeEventListener('drop', onDrop);
        if (launchErrorSink === onError) launchErrorSink = null;
      };
    },

    subscribeOpenEvents(onOpen) {
      // Warm PWA launches (launchQueue fires again while running) route here;
      // also drain anything buffered before we subscribed.
      launchSubscriber = onOpen;
      while (launchedQueue.length > 0) {
        const doc = launchedQueue.shift();
        if (doc) onOpen(doc);
      }
      return () => {
        if (launchSubscriber === onOpen) launchSubscriber = null;
      };
    },

    consumeLaunchOpen() {
      setupLaunchQueue();
      return Promise.resolve(launchedQueue.shift() ?? null);
    },

    newWindow() {
      window.open(window.location.href, '_blank', 'noopener');
    },

    notifyDocumentOpened() {
      // No-op: web is a single browsing context, there is no window/tab registry
      // to keep in sync.
    },

    openExternalLink(url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    },

    becomeDefaultApp() {
      // The web platform cannot register as an OS file handler (PWA file_handlers
      // in Phase 5 are best-effort and Chromium-only).
      return Promise.resolve('unsupported');
    },

    canSaveSilently(doc) {
      // We hold a writable target only after a successful saveAs captured one
      // (Chromium). The store only consults this once `savedOnce` is true.
      return supportsFsa && handleCache.has(doc.id);
    },

    async saveExisting(doc, text) {
      const handle = handleCache.get(doc.id);
      if (!handle) throw new Error('No writable target for this document.');
      if (!(await ensureWritePermission(handle))) {
        throw new Error('Permission to write the file was denied.');
      }
      await writeHandle(handle, text);
      const saved = await makeDocument(handle.name, new TextEncoder().encode(text), handle.name);
      handleCache.set(saved.id, handle); // re-key the writable target to the new id
      return saved;
    },

    async saveAs(origin, suggestedName, text) {
      if (supportsFsa && window.showSaveFilePicker) {
        const originHandle = origin ? handleCache.get(origin.id) : undefined;
        let handle: FileSystemFileHandle;
        try {
          handle = await window.showSaveFilePicker({
            suggestedName,
            types: PICKER_TYPES,
            ...(originHandle ? { startIn: originHandle } : {}),
          });
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return null;
          throw err;
        }
        await writeHandle(handle, text);
        const saved = await makeDocument(handle.name, new TextEncoder().encode(text), handle.name);
        handleCache.set(saved.id, handle);
        return saved;
      }
      // No File System Access API (Firefox/Safari): download. No writable target
      // is captured, so canSaveSilently stays false and every save downloads.
      downloadText(suggestedName, text);
      return makeDocument(suggestedName, new TextEncoder().encode(text), null);
    },
  };
}

async function handleDrop(
  e: DragEvent,
  onOpen: (doc: OpenedDocument) => void,
  onError: (message: string) => void,
): Promise<void> {
  const item = e.dataTransfer?.items[0];
  const file = e.dataTransfer?.files[0];
  try {
    let handle: FileSystemFileHandle | undefined;
    if (item?.getAsFileSystemHandle) {
      const h = await item.getAsFileSystemHandle();
      if (h && h.kind === 'file') handle = h as FileSystemFileHandle;
    }
    const resolvedFile = handle ? await handle.getFile() : file;
    if (!resolvedFile) return;
    // fileToDocument validates the extension (and size/UTF-8) centrally.
    const doc = await fileToDocument(resolvedFile, handle);
    onOpen(doc);
  } catch (err) {
    onError(err instanceof DocumentLoadError ? err.message : 'Could not read the dropped file.');
  }
}
